import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function StocksAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Historical Prices - Stocks API" description="Get historical OHLCV price data with the Stocks API. Access multi-time-frame equity price history for analysis and backtesting."  canonicalUrl="https://axionquant.com/docs/01-market-data/stocks/03-historical-prices" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Stocks API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
           The Stocks API provides comprehensive equity market data for publicly traded companies worldwide. This API supports multiple time frame aggregations. Essential for stock analysis, portfolio management, and trading strategy development.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Get Historical Stock Price Data */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /stocks/:ticker/prices
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves comprehensive historical price data for a specific stock. This powerful endpoint supports multiple time frame aggregations (daily, weekly, monthly, quarterly, yearly) and time-based filtering.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Stock ticker symbol (e.g., &quot;AAPL&quot;, &quot;MSFT&quot;, &quot;GOOGL&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800 align-top">frame</td>
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">string</td>
                      <td className="px-4 py-3 text-sm align-top">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <p className="mb-2">Time frame for price aggregation. Default: <code className="bg-gray-100 px-1 rounded">daily</code>.</p>
                        <table className="w-full text-xs mt-1 border-collapse">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="text-left px-2 py-1 font-semibold text-gray-700 border border-gray-200">Value(s)</th>
                              <th className="text-left px-2 py-1 font-semibold text-gray-700 border border-gray-200">Resolves to</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="px-2 py-1 border border-gray-200 font-mono">daily, day, d</td>
                              <td className="px-2 py-1 border border-gray-200 text-gray-600">daily</td>
                            </tr>
                            <tr>
                              <td className="px-2 py-1 border border-gray-200 font-mono">weekly, w</td>
                              <td className="px-2 py-1 border border-gray-200 text-gray-600">week</td>
                            </tr>
                            <tr>
                              <td className="px-2 py-1 border border-gray-200 font-mono">monthly, month, m</td>
                              <td className="px-2 py-1 border border-gray-200 text-gray-600">month</td>
                            </tr>
                            <tr>
                              <td className="px-2 py-1 border border-gray-200 font-mono">quarterly, quarter, q</td>
                              <td className="px-2 py-1 border border-gray-200 text-gray-600">quarter</td>
                            </tr>
                            <tr>
                              <td className="px-2 py-1 border border-gray-200 font-mono">yearly, year, y</td>
                              <td className="px-2 py-1 border border-gray-200 text-gray-600">year</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800 align-top">to</td>
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">string</td>
                      <td className="px-4 py-3 text-sm align-top">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        End date for historical data (ISO 8601 format). If not provided, returns data up to the latest available.
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800 align-top">from</td>
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">string</td>
                      <td className="px-4 py-3 text-sm align-top">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Start date for historical data (ISO 8601 format). Automatically clamped by the system if too far in the past.
                      </td>
                      <td className="px-4 py-3 text-sm align-top">
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/stocks/AAPL/prices', '/stocks/MSFT/prices?frame=weekly&from=2024-06-01&to=2024-12-31']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">time <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">ISO 8601 timestamp of the data point</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ticker <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Stock ticker symbol</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">open <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Opening price (decimal string)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">high <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Highest price during the period</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">low <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Lowest price during the period</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">close <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Closing price</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">volume <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Number of shares traded</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Historical Stock Prices" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Historical Stock Prices</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="stocks/prices"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response (Daily Frame)</p>
                <Code id="01-market-data/stocks/historical-prices"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
